import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import {
  classifyAddStudyExtract,
  snapshotLockedPatient,
} from "@/lib/addStudyMismatch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sonner toast id — dismissed when leaving /upload */
export const UPLOAD_EXTRACT_TOAST_ID = "upload-extract";

const isOnUploadPage = () => (
  typeof window !== "undefined"
  && window.location.pathname.startsWith("/upload")
);

const isAbortError = (err) => (
  axios.isCancel?.(err)
  || err?.code === "ERR_CANCELED"
  || err?.name === "CanceledError"
  || err?.name === "AbortError"
);

/**
 * Global upload/extraction queue so draft upload + DICOM extraction keep running
 * after leaving /upload (Google Drive–style background progress).
 * Also exposes isUploadActive for the axios auth layer (JWT refresh mid-upload).
 */
const UploadProgressContext = createContext(null);

const MAX_POLL_MS = 15 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;

export function UploadProgressProvider({ children }) {
  const activeRef = useRef(false);
  const cancelledIdsRef = useRef(new Set());
  // Survives Upload page remounts so "Patient already exists" is not shown again
  // for the same extracted draft after OK / navigate away / return.
  const patientExistsNotifiedIdsRef = useRef(new Set());
  /** @type {React.MutableRefObject<Map<string, AbortController>>} */
  const abortControllersRef = useRef(new Map());
  const [files, setFiles] = useState([]);
  const [trayMinimized, setTrayMinimized] = useState(false);
  const [trayDismissed, setTrayDismissed] = useState(false);
  /** Survives leaving /upload so share still runs after extract / mismatch review. */
  const [shareTargetUsers, setShareTargetUsers] = useState([]);
  /** File id waiting for Add Study / Upload page to mount and open the mismatch modal. */
  const [pendingMismatchFileId, setPendingMismatchFileId] = useState(null);
  /** File id waiting for Patient Detail to reopen Add Study and finish save. */
  const [pendingAddStudyFileId, setPendingAddStudyFileId] = useState(null);
  /** Opens the patient-mismatch dialog for a file (registered by Upload page / Add Study). */
  const patientMismatchOpenerRef = useRef(null);
  /** Closes Patient Detail Add Study / mismatch host (registered by PatientDetail). */
  const embeddedUploadCloserRef = useRef(null);

  const registerPatientMismatchOpener = useCallback((handler) => {
    const nextHandler = typeof handler === "function" ? handler : null;
    patientMismatchOpenerRef.current = nextHandler;

    if (nextHandler) {
      setPendingMismatchFileId((pendingId) => {
        if (!pendingId) return null;
        const idToOpen = pendingId;
        window.queueMicrotask(() => {
          // Always call the latest registered opener — identity can change between
          // schedule and flush when Upload re-renders (modality / study fields).
          const opener = patientMismatchOpenerRef.current;
          if (typeof opener === "function") {
            opener(idToOpen);
            return;
          }
          // Host unmounted before flush — re-queue so the next mount can open.
          setPendingMismatchFileId((current) => current ?? idToOpen);
        });
        return null;
      });
    }

    return () => {
      if (patientMismatchOpenerRef.current === nextHandler) {
        patientMismatchOpenerRef.current = null;
      }
    };
  }, []);

  const registerEmbeddedUploadCloser = useCallback((handler) => {
    const nextHandler = typeof handler === "function" ? handler : null;
    embeddedUploadCloserRef.current = nextHandler;
    return () => {
      if (embeddedUploadCloserRef.current === nextHandler) {
        embeddedUploadCloserRef.current = null;
      }
    };
  }, []);

  const openPatientMismatchReview = useCallback((fileId) => {
    if (!fileId) return;
    if (typeof patientMismatchOpenerRef.current === "function") {
      patientMismatchOpenerRef.current(fileId);
      return;
    }
    // Upload / Add Study is not mounted — queue the review and let the page reopen it.
    setPendingMismatchFileId(fileId);
  }, []);

  /**
   * Review mismatch via patient-page popup when possible.
   * Always queue the file id first, then close any embedded host so PatientDetail
   * remounts a fresh mismatch host and opens the dialog. Calling the opener in a
   * microtask after closer raced with unmount and dropped the open.
   */
  const openLockedPatientMismatchReview = useCallback((fileId) => {
    if (!fileId) return;
    setPendingMismatchFileId(fileId);
    if (typeof embeddedUploadCloserRef.current === "function") {
      embeddedUploadCloserRef.current();
    }
  }, []);

  const clearPendingMismatchReview = useCallback(() => {
    setPendingMismatchFileId(null);
  }, []);

  /** Reopen Add Study on the locked patient so a ready / needs-details file can finish saving. */
  const openFinishAddStudyUpload = useCallback((fileId) => {
    if (!fileId) return;
    setPendingAddStudyFileId(fileId);
    setTrayDismissed(false);
    setTrayMinimized(false);
    if (typeof embeddedUploadCloserRef.current === "function") {
      embeddedUploadCloserRef.current();
    }
  }, []);

  const clearPendingAddStudyFile = useCallback(() => {
    setPendingAddStudyFileId(null);
  }, []);

  const setUploadActive = useCallback((active) => {
    activeRef.current = active;
  }, []);

  const isUploadActive = useCallback(() => activeRef.current, []);

  const hasPatientExistsBeenNotified = useCallback((id) => (
    patientExistsNotifiedIdsRef.current.has(id)
  ), []);

  const markPatientExistsNotified = useCallback((id) => {
    if (!id) return;
    patientExistsNotifiedIdsRef.current.add(id);
  }, []);

  const abortFileRequest = useCallback((id) => {
    const controller = abortControllersRef.current.get(id);
    if (!controller) return;
    controller.abort();
    abortControllersRef.current.delete(id);
  }, []);

  const cancelFile = useCallback((id) => {
    if (!id) return;
    cancelledIdsRef.current.add(id);
    patientExistsNotifiedIdsRef.current.delete(id);
    abortFileRequest(id);
  }, [abortFileRequest]);

  const updateFile = useCallback((id, patch) => {
    // Ignore progress/status patches after cancel so they cannot resurrect removed rows.
    if (cancelledIdsRef.current.has(id)) return;
    setFiles((prev) => {
      if (!prev.some((f) => f.id === id)) return prev;
      return prev.map((f) => (f.id === id ? { ...f, ...patch } : f));
    });
  }, []);

  const removeFile = useCallback((id) => {
    cancelFile(id);
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, [cancelFile]);

  const clearCompleted = useCallback(() => {
    setFiles((prev) => prev.filter((f) => f.status !== "completed"));
  }, []);

  const dismissTray = useCallback(() => {
    // Abort every in-flight request, then clear the queue (no progress patches can resurrect rows).
    for (const id of [...abortControllersRef.current.keys()]) {
      cancelFile(id);
    }
    setFiles((prev) => {
      for (const f of prev) {
        cancelFile(f.id);
      }
      return [];
    });
    setTrayDismissed(true);
    setUploadActive(false);
    setShareTargetUsers([]);
    toast.dismiss(UPLOAD_EXTRACT_TOAST_ID);
  }, [cancelFile, setUploadActive]);

  const applyDraftResultToFile = useCallback((itemId, draftId, data) => {
    if (cancelledIdsRef.current.has(itemId)) return;

    setFiles((prev) => {
      if (!prev.some((f) => f.id === itemId)) return prev;
      return prev.map((f) => {
        if (f.id !== itemId) return f;
        const patient_details = data?.patient_details ?? null;
        const next = {
          ...f,
          status: "extracted",
          progress: 100,
          draft_id: draftId,
          patient_details,
          anatomy_details: data?.anatomy_details ?? null,
          phase: "ready",
        };
        const isAddStudy = (
          f.origin === "add_study"
          || f.queue === "add_study"
          || Boolean(f.locked_patient_uhid)
        );
        if (!isAddStudy || f.patient_mismatch) return next;
        const locked = f.locked_patient || { uhid: f.locked_patient_uhid };
        const classified = classifyAddStudyExtract(locked, patient_details, f);
        if (!classified) return next;
        return {
          ...next,
          ...classified,
          queue: f.queue || "add_study",
          locked_patient_uhid: f.locked_patient_uhid || locked.uhid || "",
        };
      });
    });
  }, []);

  const pollDraftStatus = useCallback(async (itemId, draftId, signal) => {
    const started = Date.now();

    while (Date.now() - started < MAX_POLL_MS) {
      if (cancelledIdsRef.current.has(itemId) || signal?.aborted) return;

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      if (cancelledIdsRef.current.has(itemId) || signal?.aborted) return;

      const pollRes = await axios.get(`${API}/upload/draft/${draftId}/status`, {
        signal,
      });
      const { status, error } = pollRes.data ?? {};

      if (status === "ready") {
        applyDraftResultToFile(itemId, draftId, pollRes.data);
        return { ok: true, data: pollRes.data };
      }
      if (status === "failed") {
        throw new Error(error || "Processing failed");
      }
    }

    throw new Error("Processing timed out — please try again");
  }, [applyDraftResultToFile]);

  const startExtract = useCallback(async (item, modality) => {
    if (!modality) {
      toast.error("Select a modality");
      return { ok: false };
    }
    if (!item?.id || !item?.file) {
      toast.error("Invalid file");
      return { ok: false };
    }

    cancelledIdsRef.current.delete(item.id);
    abortFileRequest(item.id);

    const controller = new AbortController();
    abortControllersRef.current.set(item.id, controller);

    setTrayDismissed(false);
    setTrayMinimized(false);

    updateFile(item.id, {
      status: "uploading",
      progress: 20,
      phase: "uploading",
      modality,
      error: null,
    });

    try {
      const formData = new FormData();
      formData.append("file", item.file);
      formData.append("modality", modality);

      const res = await axios.post(`${API}/upload/draft`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        signal: controller.signal,
        onUploadProgress: (p) => {
          if (cancelledIdsRef.current.has(item.id)) return;
          const pct = Math.round((p.loaded * 100) / (p.total ?? 1));
          updateFile(item.id, { progress: pct, phase: "uploading" });
        },
      });

      if (cancelledIdsRef.current.has(item.id) || controller.signal.aborted) {
        return { ok: false, cancelled: true };
      }

      const draftId = res.data?.draft_id;
      if (!draftId) {
        if (isOnUploadPage()) {
          toast.error("No draft ID returned from extraction", { id: UPLOAD_EXTRACT_TOAST_ID });
        }
        updateFile(item.id, { status: "error", progress: 0, phase: "error", error: "No draft ID" });
        return { ok: false };
      }

      if (res.data?.status === "processing") {
        updateFile(item.id, {
          status: "uploading",
          progress: 100,
          phase: "extracting",
          draft_id: draftId,
        });
        const pollResult = await pollDraftStatus(item.id, draftId, controller.signal);
        if (cancelledIdsRef.current.has(item.id) || controller.signal.aborted) {
          return { ok: false, cancelled: true };
        }
        // Success toast only from Upload page (so it doesn't linger on other routes)
        return { ok: true, draftId, data: pollResult?.data };
      }

      applyDraftResultToFile(item.id, draftId, res.data);
      return { ok: true, draftId, data: res.data };
    } catch (err) {
      if (
        cancelledIdsRef.current.has(item.id)
        || controller.signal.aborted
        || isAbortError(err)
      ) {
        return { ok: false, cancelled: true };
      }

      const detail = formatApiErrorDetail(err?.response?.data?.detail)
        || (err instanceof Error ? err.message : null)
        || "Extraction failed";
      if (isOnUploadPage()) {
        toast.error(detail, { id: UPLOAD_EXTRACT_TOAST_ID });
      }
      updateFile(item.id, {
        status: "error",
        progress: 0,
        phase: "error",
        error: detail,
      });
      return { ok: false, error: detail };
    } finally {
      abortControllersRef.current.delete(item.id);
    }
  }, [updateFile, pollDraftStatus, applyDraftResultToFile, abortFileRequest]);

  const addFilesAndExtract = useCallback((items, modality, options = {}) => {
    if (!items?.length) return;
    const queue = options.queue === "add_study" ? "add_study" : "upload";
    const lockedPatientUhid = String(options.locked_patient_uhid ?? "").trim();
    const lockedPatient = snapshotLockedPatient(options.locked_patient)
      || (lockedPatientUhid ? { uhid: lockedPatientUhid } : null);
    const studyFields = options.study_fields && typeof options.study_fields === "object"
      ? options.study_fields
      : null;
    const shareTargets = Array.isArray(options.share_target_users)
      ? options.share_target_users.filter((user) => user?.id)
      : (options.share_target_user?.id ? [options.share_target_user] : []);
    setTrayDismissed(false);
    setTrayMinimized(false);
    setFiles((prev) => [...prev, ...items.map((item) => ({
      ...item,
      modality: modality || item.modality || "",
      phase: item.phase || "pending",
      queue: item.queue || queue,
      // Stable upload source — never rewrite this when reopening reviews.
      origin: item.origin || (queue === "add_study" ? "add_study" : "upload"),
      ...(studyFields || item.study_fields
        ? { study_fields: item.study_fields || studyFields }
        : {}),
      ...((shareTargets.length || item.share_target_users || item.share_target_user)
        ? {
          share_target_users: item.share_target_users
            || (item.share_target_user ? [item.share_target_user] : shareTargets),
        }
        : {}),
      ...(queue === "add_study" && lockedPatientUhid
        ? {
          locked_patient_uhid: item.locked_patient_uhid || lockedPatientUhid,
          ...(item.locked_patient || lockedPatient
            ? { locked_patient: item.locked_patient || lockedPatient }
            : {}),
        }
        : {}),
    }))]);
    items.forEach((item) => {
      startExtract(item, modality);
    });
  }, [startExtract]);

  const hasActiveWork = useMemo(
    () => files.some((f) => (
      f.status === "uploading"
      || f.status === "pending"
      || f.phase === "extracting"
      || f.phase === "uploading"
    )),
    [files],
  );

  const hasVisibleJobs = useMemo(
    () => files.some((f) => f.status !== "completed") || files.some((f) => f.status === "completed"),
    [files],
  );

  const showTray = !trayDismissed && files.length > 0;

  useEffect(() => {
    setUploadActive(hasActiveWork);
    return () => {
      if (!hasActiveWork) setUploadActive(false);
    };
  }, [hasActiveWork, setUploadActive]);

  // Keep auth flag accurate even if provider stays mounted with idle queue
  useEffect(() => {
    if (!hasActiveWork) setUploadActive(false);
  }, [hasActiveWork, setUploadActive]);

  const value = useMemo(() => ({
    files,
    setFiles,
    updateFile,
    removeFile,
    clearCompleted,
    addFilesAndExtract,
    startExtract,
    hasActiveWork,
    hasVisibleJobs,
    showTray,
    trayMinimized,
    setTrayMinimized,
    dismissTray,
    setTrayDismissed,
    setUploadActive,
    isUploadActive,
    hasPatientExistsBeenNotified,
    markPatientExistsNotified,
    registerPatientMismatchOpener,
    registerEmbeddedUploadCloser,
    openPatientMismatchReview,
    openLockedPatientMismatchReview,
    pendingMismatchFileId,
    clearPendingMismatchReview,
    openFinishAddStudyUpload,
    pendingAddStudyFileId,
    clearPendingAddStudyFile,
    shareTargetUsers,
    setShareTargetUsers,
  }), [
    files,
    updateFile,
    removeFile,
    clearCompleted,
    addFilesAndExtract,
    startExtract,
    hasActiveWork,
    hasVisibleJobs,
    showTray,
    trayMinimized,
    dismissTray,
    setUploadActive,
    isUploadActive,
    hasPatientExistsBeenNotified,
    markPatientExistsNotified,
    registerPatientMismatchOpener,
    registerEmbeddedUploadCloser,
    openPatientMismatchReview,
    openLockedPatientMismatchReview,
    pendingMismatchFileId,
    clearPendingMismatchReview,
    openFinishAddStudyUpload,
    pendingAddStudyFileId,
    clearPendingAddStudyFile,
    shareTargetUsers,
  ]);

  return (
    <UploadProgressContext.Provider value={value}>
      {children}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgress() {
  const ctx = useContext(UploadProgressContext);
  if (!ctx) throw new Error("useUploadProgress must be used within UploadProgressProvider");
  return ctx;
}
